import { useRef } from 'react'

/**
 * The git / local-folder / zip fields for registering a reference source.
 *
 * ONE source of this UI: the workbench "Replicate from Source Code" tab and
 * the Ask-AI composer's "Add a source" menu both render it, so the two entry
 * points can never drift. It owns no registration logic — the caller decides
 * what "register" means (create a project first, attach to the current one, …)
 * and calls the referenceSources API itself.
 */

export type SourceMode = 'git' | 'path' | 'zip'

export interface ReferenceSourceValue {
  mode: SourceMode
  gitUrl: string
  gitRef: string
  localPath: string
  zipFile: File | null
}

export const emptyReferenceSource = (mode: SourceMode = 'git'): ReferenceSourceValue => ({
  mode, gitUrl: '', gitRef: '', localPath: '', zipFile: null,
})

/** The user-facing reason this value can't be registered yet, or null. */
export function referenceSourceError(v: ReferenceSourceValue): string | null {
  if (v.mode === 'git' && !v.gitUrl.trim()) return 'Enter the git repository URL'
  if (v.mode === 'path' && !v.localPath.trim()) return 'Enter the absolute folder path'
  if (v.mode === 'zip' && !v.zipFile) return 'Choose a .zip of the source code'
  return null
}

/** Short human label for the chosen source, for chips and confirmations. */
export function referenceSourceLabel(v: ReferenceSourceValue): string {
  if (v.mode === 'git') return v.gitUrl.trim() || 'git repository'
  if (v.mode === 'path') return v.localPath.trim() || 'local folder'
  return v.zipFile?.name || 'source archive'
}

export default function ReferenceSourceFields({
  value,
  onChange,
  disabled = false,
  uploadPct = 0,
  modes = ['git', 'path', 'zip'],
}: {
  value: ReferenceSourceValue
  onChange: (v: ReferenceSourceValue) => void
  disabled?: boolean
  /** Zip upload progress (0-100) while the caller is uploading. */
  uploadPct?: number
  /** Restrict the offered modes (e.g. ['zip'] for an "upload a zip" entry). */
  modes?: SourceMode[]
}) {
  const zipInputRef = useRef<HTMLInputElement>(null)
  const patch = (p: Partial<ReferenceSourceValue>) => onChange({ ...value, ...p })

  const MODE_META: Record<SourceMode, { label: string; icon: string }> = {
    git: { label: 'Git URL', icon: 'code' },
    path: { label: 'Local Folder', icon: 'folder' },
    zip: { label: 'Upload Zip', icon: 'folder_zip' },
  }

  return (
    <>
      {modes.length > 1 && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          {modes.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => patch({ mode: m })}
              disabled={disabled}
              className="btn btn-sm"
              style={{
                flex: 1,
                background: value.mode === m ? 'var(--color-accent)' : 'var(--color-bg)',
                color: value.mode === m ? '#fff' : 'var(--color-text-secondary)',
                border: '1px solid var(--color-border)',
              }}
            >
              <span className="material-icons" style={{ fontSize: 17 }}>{MODE_META[m].icon}</span>
              {MODE_META[m].label}
            </button>
          ))}
        </div>
      )}

      {value.mode === 'git' && (
        <>
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="src-git-url">Git Repository URL</label>
            <input
              id="src-git-url"
              type="text"
              value={value.gitUrl}
              onChange={(e) => patch({ gitUrl: e.target.value })}
              placeholder="https://github.com/acme/erp"
              disabled={disabled}
            />
          </div>
          <div style={{ marginBottom: '1.25rem' }}>
            <label htmlFor="src-git-ref">Branch or Tag (optional)</label>
            <input
              id="src-git-ref"
              type="text"
              value={value.gitRef}
              onChange={(e) => patch({ gitRef: e.target.value })}
              placeholder="main"
              disabled={disabled}
            />
          </div>
        </>
      )}

      {value.mode === 'path' && (
        <div style={{ marginBottom: '1.25rem' }}>
          <label htmlFor="src-local-path">Absolute Folder Path (on the server)</label>
          <input
            id="src-local-path"
            type="text"
            value={value.localPath}
            onChange={(e) => patch({ localPath: e.target.value })}
            placeholder="/Users/me/dev/legacy-erp"
            disabled={disabled}
          />
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, display: 'block' }}>
            The folder is read in place (never copied or modified). It must exist on the
            machine running the Twasta backend.
          </span>
        </div>
      )}

      {value.mode === 'zip' && (
        <div style={{ marginBottom: '1.25rem' }}>
          <label>Source Code Archive (.zip, up to 500 MB)</label>
          <input
            ref={zipInputRef}
            type="file"
            accept=".zip"
            style={{ display: 'none' }}
            onChange={(e) => patch({ zipFile: e.target.files?.[0] || null })}
          />
          <div
            onClick={() => { if (!disabled) zipInputRef.current?.click() }}
            style={{
              border: '1px dashed var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: '1.25rem',
              textAlign: 'center',
              cursor: disabled ? 'default' : 'pointer',
              background: 'var(--color-bg)',
            }}
          >
            <span className="material-icons" style={{ fontSize: 29, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 6 }}>
              upload_file
            </span>
            <span style={{ fontSize: '0.9375rem', color: 'var(--color-text)' }}>
              {value.zipFile
                ? `${value.zipFile.name} (${Math.round(value.zipFile.size / (1024 * 1024))} MB)`
                : 'Click to choose a .zip file'}
            </span>
            {uploadPct > 0 && (
              <div style={{ marginTop: 8, fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                Uploading... {uploadPct}%
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
